import React, { useState, useEffect, useCallback } from "react";
import { Offcanvas, Form, Button, Card, Image, Modal } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import { jwtDecode } from 'jwt-decode';
import { userService } from '../services/userService';
import { bulkUploadService } from '../services/bulkUploadService';
import connectorService from '../services/connectorService';
import upload from "../assets/images/icons/upload.png";
import success from "../assets/images/icons/success.png";
import { MdOutlineDownloadForOffline } from "react-icons/md";

const BulkUrlOffCanvas = ({ show, onHide }) => {
  const [file, setFile] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [accountId, setAccountId] = useState(null);
  const [showSftpImportModal, setShowSftpImportModal] = useState(false);
  const [sftpConfigs, setSftpConfigs] = useState([]);
  const [sftpLoading, setSftpLoading] = useState(false);
  const [selectedSftpConfigId, setSelectedSftpConfigId] = useState('');
  const [remoteFilePath, setRemoteFilePath] = useState('');
  const [isImportingFromSftp, setIsImportingFromSftp] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (show) {
      fetchAccountId();
    }
  }, [show]);

  const fetchAccountId = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const decodedToken = jwtDecode(token);
      const username = decodedToken.sub;

      const userData = await userService.getUserByUsername(username);
      setAccountId(userData.accountId);
    } catch (error) {
      console.error('Error fetching account ID:', error);
      toast.error('Failed to fetch account information');
    }
  };

  const handleDownloadTemplate = async () => {
    setIsDownloadingTemplate(true);
    try {
      const blob = await bulkUploadService.downloadTemplate();
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Template downloaded successfully');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const openSftpImportModal = async () => {
    setShowSftpImportModal(true);
    if (!accountId) return;
    setSftpLoading(true);
    try {
      const response = await connectorService.getConfigurationsByType(accountId, 'import');
      const configs = response?.data || response || [];
      setSftpConfigs(Array.isArray(configs) ? configs : []);
      if (Array.isArray(configs) && configs.length > 0) {
        setSelectedSftpConfigId(configs[0].id);
      }
    } catch (error) {
      console.error('Error loading SFTP configurations:', error);
      toast.error('Failed to load SFTP configurations');
    } finally {
      setSftpLoading(false);
    }
  };

  const handleImportFromSftp = async () => {
    if (!selectedSftpConfigId || !remoteFilePath.trim()) {
      toast.error('Select an SFTP configuration and enter the remote file path');
      return;
    }
    setIsImportingFromSftp(true);
    try {
      await bulkUploadService.importFromSftp({
        accountId,
        sftpConfigId: selectedSftpConfigId,
        remoteFilePath: remoteFilePath.trim()
      });
      toast.success('Import started successfully');
      setShowSftpImportModal(false);
      setRemoteFilePath('');
    } catch (error) {
      console.error('Error importing from SFTP:', error);
      toast.error(error.response?.data?.message || 'Failed to import from SFTP');
    } finally {
      setIsImportingFromSftp(false);
    }
  };

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      setErrorMessage("Invalid file type! Only CSV and Excel files are allowed.");
      return;
    }
    setFile(acceptedFiles[0]);
    setUploadSuccess(false); 
    setErrorMessage(""); 
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      "text/csv": [".csv"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    },
  });

  const handleSubmit = async () => {
    if (file) {
      setIsUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await bulkUploadService.uploadBulkUrls(formData);

        if (response) {
          setUploadSuccess(true);
          setFile(null);
          toast.success('File uploaded successfully! Redirecting to Bulk URL page...');
          
          setTimeout(() => {
            onHide();
            navigate('/urlShorten?tab=bulk');
          }, 1500);
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        setErrorMessage('Failed to upload file. Please try again.');
        toast.error('Failed to upload file');
        setUploadSuccess(false);
      } finally {
        setIsUploading(false);
      }
    }
  };

  const resetForm = () => {
    setFile(null);
    setUploadSuccess(false);
    setErrorMessage("");
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  return (
    <>
    <Offcanvas show={show} onHide={handleClose} placement="end">
      <Offcanvas.Header closeButton className="border-bottom bg-white">
        <Offcanvas.Title className="fw-600" style={{fontSize:'16px'}}>Bulk URL Upload</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <div className="d-flex justify-content-end mb-3">
          <Button 
            variant="outline-success" 
            onClick={handleDownloadTemplate}
            disabled={isDownloadingTemplate}
          >
            {isDownloadingTemplate ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Downloading...
              </>
            ) : (
              <>
                Download Template <MdOutlineDownloadForOffline className="fs-14" />
              </>
            )}
          </Button>
          <Button 
            variant="outline-success" 
            className="ms-2"
            onClick={openSftpImportModal}
          >
            Import from SFTP
          </Button>
        </div>

        <Card className="br-radius-12 border-0 shadow-none">
          <Card.Body className="p-0">
            <div
              {...getRootProps()}
              className="p-4 border-dashed d-flex align-items-center justify-content-center bg-f1f1f1 text-center rounded"
              style={{ 
                minHeight: '200px', 
                borderColor: "#f1f1f1", 
                border: "1px dashed rgb(168, 176, 185)", 
                cursor: "pointer" 
              }}
            >
              <input {...getInputProps()} />
              {uploadSuccess ? (
                <div className="flex-column justify-content-center">
                  <Image width="46" className="mb-3 d-block m-auto" src={success} />
                  <p className="fs-14 mb-0 text-success fw-600">File Uploaded Successfully!</p>
                  <p className="fs-12 mb-0 text-muted">Redirecting to Bulk URL page...</p>
                </div>
              ) : file ? (
                <div className="flex-column justify-content-center">
                  <div className="mb-3">
                    <i className="bi bi-file-earmark-text" style={{ fontSize: "2rem", color: "#28a745" }}></i>
                  </div>
                  <p className="fs-14 mb-0 fw-600">{file.name}</p>
                  <p className="fs-12 mb-0 text-muted">Ready to upload</p>
                </div>
              ) : isDragActive ? (
                <div>
                  <Image width="56" className="mb-3" src={upload} />
                  <p className="fs-14 mb-0">Drop the file here...</p>
                </div>
              ) : (
                <div>
                  <Image width="56" className="mb-3" src={upload} />
                  <p className="fs-12 mb-1">
                    Drag & Drop your filled template
                  </p>
                  <p className="fs-12 mb-0 text-success">
                    or Click here to browse files
                  </p>
                </div>
              )}
            </div>

            {errorMessage && (
              <div className="alert alert-danger mt-3 fs-12">
                {errorMessage}
              </div>
            )}

            <div className="mt-3">
              <div className="text-242D35 fs-12">
                <strong className="GeneralSansMedium">Note:</strong>
                <ul className="ps-3 fs-11 mt-2">
                  <li>For better performance, up to 500,000 records can be uploaded at a time.</li>
                  <li>File must be in CSV format.</li>
                  <li>Please download and use the provided template for best results.</li>
                  <li>If you have trouble uploading, please contact support.</li>
                </ul>
              </div>
            </div>

            <div className="d-flex justify-content-end mt-4">
              <Button variant="outline-secondary" className="me-2" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="success"
                onClick={handleSubmit}
                disabled={!file || isUploading || uploadSuccess}
              >
                {isUploading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Uploading...
                  </>
                ) : uploadSuccess ? (
                  'Redirecting...'
                ) : (
                  'Upload File'
                )}
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Offcanvas.Body>
    </Offcanvas>

    <Modal show={showSftpImportModal} onHide={() => setShowSftpImportModal(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-16">Import from SFTP</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {sftpLoading ? (
          <div className="d-flex align-items-center"><span className="spinner-border spinner-border-sm me-2" /> Loading configurations...</div>
        ) : (
          <>
            <Form.Group className="mb-3">
              <Form.Label className="fs-12">SFTP Configuration</Form.Label>
              <Form.Select
                value={selectedSftpConfigId}
                onChange={(e) => setSelectedSftpConfigId(e.target.value)}
              >
                <option value="">Select Configuration</option>
                {sftpConfigs.map((cfg) => (
                  <option key={cfg.id} value={cfg.id}>
                    {cfg.name || `${cfg.host} (${cfg.username})`}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label className="fs-12">Remote File Path</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., /imports/bulk.csv"
                value={remoteFilePath}
                onChange={(e) => setRemoteFilePath(e.target.value)}
              />
              <Form.Text className="fs-11">CSV file path accessible by the configured SFTP server.</Form.Text>
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={() => setShowSftpImportModal(false)}>Cancel</Button>
        <Button variant="success" onClick={handleImportFromSftp} disabled={isImportingFromSftp || sftpLoading}>
          {isImportingFromSftp ? (<><span className="spinner-border spinner-border-sm me-2" /> Importing...</>) : 'Import'}
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default BulkUrlOffCanvas;